import { useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'

import { apiGetUserBalances, apiGetUserBalancesForChain } from '@src/api/machprotocol.api'
import { BALANCES_REFRESH_INTERVAL } from '@src/constants/application'
import { ALL_BALANCES_QUERY_KEY } from '@src/constants/application'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { IBalanceResponse } from '@src/types/balance'
import { Address } from '@src/types/token'
import {
    createContextLogger,
    isBitcoinAddress,
    isEVMAddress,
    isFeatureEnabled,
    isSolanaAddress,
    isTronAddress,
} from '@src/utils'
import { useTokensStore } from '@src/zustand_stores'

const logger = createContextLogger('useFetchAllBalances')

export type WalletAddresses = {
    evmWalletAddress?: string
    tronWalletAddress?: string
    solanaWalletAddress?: string
}

const loadAllBalances = async (walletAddresses: WalletAddresses): Promise<IBalanceResponse[]> => {
    logger.info('loadAllBalances called. walletAddresses: ', { walletAddresses })

    // Array to hold promises for each valid wallet address
    const promises: Promise<IBalanceResponse[]>[] = []
    let connectedAddressesCount = 0

    // Guard against null or undefined wallet addresses object
    if (!walletAddresses) {
        logger.info('No wallet addresses object provided')
        return []
    }

    // Check and add EVM address promise
    if (walletAddresses.evmWalletAddress) {
        // Skip if the address is not valid EVM address
        if (isEVMAddress(walletAddresses.evmWalletAddress)) {
            // Skip API calls if the address is a Bitcoin address
            if (!isBitcoinAddress(walletAddresses.evmWalletAddress)) {
                const getEVMFilteredBalances = async (): Promise<IBalanceResponse[]> => {
                    const res = await apiGetUserBalances(walletAddresses.evmWalletAddress)

                    // Filter out balances that belong to the 'solana', 'tron' or 'bitcoin' chains
                    // NOTE: This is necessary, since we're passing an EVM wallet address, for which
                    // the API returns zero balances for solana and tron chains, and since we can't
                    // control promise resolution order, the zero balances might overwrite correctly loaded
                    // solana and tron balances.
                    return res.filter(b => b.chain !== 'solana' && b.chain !== 'tron' && b.chain !== 'bitcoin')
                }
                promises.push(getEVMFilteredBalances())
                connectedAddressesCount++
            }
        }
    }

    // Check and add Tron address promise
    if (walletAddresses.tronWalletAddress) {
        // Skip if the address is not valid Tron address
        if (isTronAddress(walletAddresses.tronWalletAddress)) {
            // Skip API calls if the address is a Bitcoin address
            if (!isBitcoinAddress(walletAddresses.tronWalletAddress)) {
                promises.push(apiGetUserBalancesForChain(walletAddresses.tronWalletAddress, 'tron'))
                connectedAddressesCount++
            }
        }
    }

    // Check and add Solana address promise
    if (walletAddresses.solanaWalletAddress) {
        // Skip if the address is not valid Solana address
        if (isSolanaAddress(walletAddresses.solanaWalletAddress)) {
            // Skip API calls if the address is a Bitcoin address
            if (!isBitcoinAddress(walletAddresses.solanaWalletAddress)) {
                promises.push(apiGetUserBalancesForChain(walletAddresses.solanaWalletAddress, 'solana'))
                connectedAddressesCount++
            }
        }
    }

    // Log details about the promises and wallet connections
    logger.info(`Number of promises built: ${promises.length}`)
    logger.info(`Number of connected addresses: ${connectedAddressesCount}`)
    const connectedWallets = Object.values(walletAddresses).filter(address => !!address).length
    logger.info(`Number of connected wallets: ${connectedWallets}`)

    try {
        // Execute all promises concurrently
        const results = await Promise.all(promises)
        // Flatten the array of arrays into a single array of balance responses
        const loadedBalances = results.reduce<IBalanceResponse[]>((acc, result) => acc.concat(result), [])

        logger.info('All balances loaded', { loadedBalances })

        return loadedBalances
    } catch (error) {
        logger.error('Failed to load balanceResponses', { error })
        throw error
    }

    // Wait for all promises to resolve and combine results
    const results = await Promise.all(promises)
    // Flatten the array of arrays
    return results.flat()
}

/**
 * Tanstack query for fetching tokens
 */
function useAllBalancesQuery() {
    const walletAddresses = useWalletConnections()
    const { tokens: zustandTokens, isLoading: zustandTokensLoading } = useTokensStore()
    const isEnabled =
        !zustandTokensLoading && !!zustandTokens && Array.isArray(zustandTokens) && zustandTokens.length > 0

    return useQuery({
        queryKey: [ALL_BALANCES_QUERY_KEY, JSON.stringify(walletAddresses)],
        queryFn: () => {
            return loadAllBalances(walletAddresses)
        },
        enabled: isEnabled,
        staleTime: BALANCES_REFRESH_INTERVAL,
        refetchInterval: BALANCES_REFRESH_INTERVAL,
        refetchOnWindowFocus: true,
    })
}

export function useFetchAllBalances() {
    const { data, isLoading, error } = useAllBalancesQuery()
    const resetTokenBalancesWithBalances = useTokensStore(state => state.resetTokenBalancesWithBalances)

    useEffect(() => {
        if (data) {
            resetTokenBalancesWithBalances(data)
        }
    }, [data, resetTokenBalancesWithBalances])

    return { isLoading, error }
}
